import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BroadcastIcon, ArrowDownRightIcon, ArrowUpRightIcon, ArrowSquareOutIcon,
  ChartLineUpIcon, SpinnerGapIcon, CaretDownIcon, CaretUpIcon, UsersThreeIcon, RocketLaunchIcon,
  BellSimpleIcon, BellSlashIcon, TargetIcon, SkullIcon, CurrencyDollarIcon, GraduationCapIcon,
} from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { InfoTip } from "./InfoTip";
import { useProvenDevs } from "../lib/useProvenDevs";
import { useAuth } from "../context/AuthContext";
import { fetchCommandCenter } from "../lib/api";
import { useTrash } from "../lib/useTrash";
import { QuickBuyLink, QuickSellLink } from "./TradeButton";
import { ApeWatermark } from "./ApeWatermark";

const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;
const MILLY_WINDOW_LABEL = "6h";

const fmtUsdShort = (v) => {
  if (!v) return null;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};
const fmtAgeShort = (h) => {
  if (h == null) return null;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

// Uniform token stats line shown on every Command Center card.
const riskDot = (t) => {
  const liq = t.liquidity, age = t.ageHours;
  if (liq == null && age == null) return null;
  const vThin = liq != null && liq < 3000;
  const thinLiq = liq != null && liq < 12000;
  const veryNew = age != null && age < 0.25;
  const young = age != null && age < 3;
  const deep = liq != null && liq >= 40000;
  if (vThin || veryNew)
    return { color: "#FF3366", label: "High risk", desc: `Thin liquidity${liq != null ? ` (~$${Math.round(liq / 1000)}K)` : ""} and/or seconds-old${age != null && age < 1 ? ` (~${Math.max(1, Math.round(age * 60))}m old)` : ""} — easy to rug and hard to exit. Size tiny, take profits fast.` };
  if (thinLiq || young)
    return { color: "#F0A020", label: "Elevated risk", desc: "Shallow liquidity or still very young — real demand can still dump quickly. Watch the chart closely." };
  if (deep && age != null && age >= 3)
    return { color: "#00E599", label: "Lower risk", desc: "Deeper liquidity and past the first-hours rug window — steadier footing, but still DYOR." };
  return { color: "#F0A020", label: "Moderate risk", desc: "Middle-of-the-road liquidity and age — no glaring red flags, but nothing bulletproof. DYOR." };
};

const TokenMeta = ({ t }) => {
  const bits = [];
  const age = fmtAgeShort(t.ageHours);
  const mc = fmtUsdShort(t.marketCap);
  const liq = fmtUsdShort(t.liquidity);
  const vol = fmtUsdShort(t.vol24h ?? t.volume24h);
  if (age) bits.push(["age", age]);
  if (mc) bits.push(["mcap", mc]);
  if (liq) bits.push(["liq", liq]);
  if (vol) bits.push(["24h vol", vol]);
  const rd = riskDot(t);
  if (!bits.length && !rd) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-1 font-mono text-[10px]" data-testid={`cc-meta-${t.tokenMint}`}>
      {rd && (
        <InfoTip title={`${rd.label} play`} desc={rd.desc} color={rd.color} side="top">
          <span data-testid={`cc-risk-${t.tokenMint}`} className="inline-flex items-center gap-1 mr-0.5">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: rd.color, boxShadow: `0 0 6px ${rd.color}99` }} />
          </span>
        </InfoTip>
      )}
      {bits.map(([k, v], i) => (
        <span key={k} className="flex items-center gap-1">
          {i > 0 && <span className="text-white/15">·</span>}
          <span className="text-muted-foreground/70">{k}</span>
          <span className="text-white/85 font-semibold">{v}</span>
        </span>
      ))}
    </div>
  );
};

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.36);
  } catch { /* ignore */ }
};

const CC_CACHE_KEY = "alphaapeing_cc_cache";
const readCcCache = () => {
  try {
    const j = JSON.parse(localStorage.getItem(CC_CACHE_KEY) || "null");
    return j?.events?.length ? j : undefined;
  } catch {
    return undefined;
  }
};

export const CommandCenter = () => {
  const { user } = useAuth();
  const clientId = getClientId();
  const { isTrashed, trash } = useTrash();
  const { provenFor } = useProvenDevs();
  const [open, setOpen] = useState(() => localStorage.getItem("alphaapeing_cc_open") === "1");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("alphaapeing_cc_sound") === "1");
  const [showHidden, setShowHidden] = useState(false);
  const seenRef = useRef(null); // Set of qualifying signatures already seen (null = first load)

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem("alphaapeing_cc_open", next ? "1" : "0");
  };

  const toggleSound = async (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("alphaapeing_cc_sound", next ? "1" : "0");
    if (next) {
      beep(); // confirm + unlock audio via this user gesture
      try { if ("Notification" in window && Notification.permission === "default") await Notification.requestPermission(); } catch { /* ignore */ }
    }
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["commandCenter", clientId],
    queryFn: () => fetchCommandCenter(clientId),
    refetchInterval: 20000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    initialData: readCcCache,
    initialDataUpdatedAt: 0,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (data?.events?.length) {
      try {
        localStorage.setItem(CC_CACHE_KEY, JSON.stringify({
          events: data.events.slice(0, 40),
          suppressed: (data.suppressed || []).slice(0, 20),
        }));
      } catch { /* ignore */ }
    }
  }, [data]);

  const events = (data?.events || []).filter((e) => !e.tokenMint || !isTrashed(e.tokenMint));
  const suppressed = data?.suppressed || [];

  // Ping on NEW 🚀 running coins or 🔥 copy buys (works even while collapsed)
  useEffect(() => {
    if (!data) return;
    const qualifying = events.filter((e) => e.kind === "run" || e.kind === "fastmover" || e.kind === "cluster" || e.kind === "milly_runner" || e.kind === "graduate" || (e.kind === "trade" && e.action === "buy" && ((e.millyTier || 0) >= 5 || e.copyWorthy)));
    const keyOf = (e) => (e.kind === "cluster" ? `${e.signature}-${e.walletCount}` : e.kind === "milly_runner" ? `${e.signature}-${e.millyTier}` : e.kind === "graduate" ? e.signature : (e.millyTier ? `milly-${e.signature}-${e.millyTier}` : e.signature));
    const sigs = new Set(qualifying.map(keyOf));
    if (seenRef.current === null) { seenRef.current = sigs; return; } // skip first load
    const fresh = qualifying.filter((e) => !seenRef.current.has(keyOf(e)));
    seenRef.current = sigs;
    if (fresh.length && soundOn) {
      beep();
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const top = fresh[0];
          const title = top.kind === "run" ? `🚀 ${top.tokenSymbol} is running`
            : top.kind === "graduate" ? `🎓 ${top.tokenSymbol} just graduated & ripping`
            : top.kind === "milly_runner" ? `🏃💰 MILLY RUNNER: ${top.tokenSymbol} running + ${top.millyTier}+ SOL in`
            : top.kind === "cluster" ? `🎯 ${top.walletCount} wallets buying ${top.tokenSymbol}`
            : top.kind === "fastmover" ? (top.direction === "up" ? `🚀 ${top.tokenSymbol} ${top.multiplier}× this hour` : `🔻 ${top.tokenSymbol} dumping fast`)
            : (top.millyTier ? `💰 MILLY'S CALL: ${top.millyTier}+ SOL into ${top.tokenSymbol}` : `🔥 COPY buy: ${top.tokenSymbol}`);
          new Notification("AlphaApeing Command Center", {
            body: fresh.length > 1 ? `${title} +${fresh.length - 1} more` : title,
          });
        }
      } catch { /* ignore */ }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtVol = (v) => {
    if (!v) return "—";
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${Math.round(v)}`;
  };

  const provenBadge = (creator, mint) => {
    const p = provenFor(creator);
    if (!p) return null;
    return (
      <InfoTip title="Proven dev" desc={`${p.name} is one of your tracked devs — ${p.hitRate}% of their ${p.coins} tracked coins ran ≥2× from when we first spotted them. A proven builder.`} color="#F0C000" side="top">
        <span data-testid={`cc-proven-${mint}`} className="text-[9px] font-mono px-1.5 py-0.5 tracking-wide font-bold" style={{ background: "rgba(240,192,0,0.15)", border: "1px solid rgba(240,192,0,0.6)", color: "#F0C000" }}>⭐ {p.hitRate}% HIT</span>
      </InfoTip>
    );
  };

  const trashBtn = (mint, symbol) => (
    <button
      data-testid={`cc-trash-${mint}`}
      onClick={(e) => { e.stopPropagation(); trash(mint, symbol); }}
      title="Mark as trash/scam — hides it from here and your Radar"
      className="p-1.5 text-muted-foreground hover:text-danger transition-colors"
    >
      <SkullIcon size={15} weight="bold" />
    </button>
  );

  return (
    <div className="border border-neon/30 bg-neon/[0.03]" data-testid="command-center">
      <button
        data-testid="command-center-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-neon/[0.04] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <BroadcastIcon size={20} weight="fill" className="text-neon" />
          <span className="font-heading font-bold text-base tracking-tight">COMMAND CENTER</span>
          <span className="flex items-center gap-1.5 text-[11px] font-mono text-neon">
            <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" /> live
          </span>
          {isFetching && data && (
            <SpinnerGapIcon size={12} className="animate-spin text-muted-foreground" data-testid="cc-refreshing" />
          )}
          {open && events.length > 0 && (
            <span className="text-[11px] font-mono text-muted-foreground">· {events.length} recent</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="hidden sm:block text-xs font-mono">Your tracked wallets, in real time</span>
          <span
            role="button"
            tabIndex={0}
            data-testid="command-center-sound-toggle"
            onClick={toggleSound}
            onKeyDown={(e) => { if (e.key === "Enter") toggleSound(e); }}
            title={soundOn ? "Alert sound ON — click to mute" : "Enable sound + notifications for new 🚀 runners & 🔥 copy buys"}
            className={`p-1.5 transition-colors ${soundOn ? "text-neon" : "hover:text-white"}`}
          >
            {soundOn ? <BellSimpleIcon size={16} weight="fill" /> : <BellSlashIcon size={16} />}
          </span>
          {open ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-neon/20">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground font-mono text-sm">
              <SpinnerGapIcon size={20} className="animate-spin text-neon" /> Tuning in…
            </div>
          ) : events.length === 0 ? (
            <div className="relative overflow-hidden py-10 px-6">
              <ApeWatermark opacity={0.06} size="150px" />
              <div className="relative z-10 text-center text-muted-foreground font-mono text-sm flex flex-col items-center gap-3">
                <UsersThreeIcon size={28} className="text-muted-foreground" />
                Quiet right now. Track proven wallets on the{" "}
                <Link to="/smart-money" className="text-neon hover:underline">Smart Money page</Link>{" "}
                and running coins will surface here live.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/5 max-h-[440px] overflow-y-auto">
              {events.map((t) => {
                if (t.kind === "graduate") {
                  const G = "#22D3EE";
                  return (
                    <div
                      key={t.signature}
                      data-testid={`cc-graduate-${t.tokenMint}`}
                      className="relative flex items-center gap-3 px-5 py-4 overflow-hidden"
                      style={{ borderLeft: `4px solid ${G}`, background: `linear-gradient(90deg, ${G}22, ${G}05)` }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center border shrink-0 animate-pulse" style={{ borderColor: G, color: G }}>
                        <GraduationCapIcon size={17} weight="fill" />
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wide font-bold animate-pulse" style={{ background: `${G}2e`, border: `1px solid ${G}`, color: G }}>
                            🎓 FRESH GRADUATE
                          </span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                          <span className="text-muted-foreground text-xs">· ${(t.marketCap || 0).toLocaleString()} mcap</span>
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: G }}>
                          Graduated {t.gradAgeMin}m ago & ripping · ${(t.volH1 || 0).toLocaleString()} vol/1h · {t.buys5} buys/5m
                          {typeof t.changeH1 === "number" ? <span className="text-muted-foreground"> · {t.changeH1 >= 0 ? "+" : ""}{Math.round(t.changeH1)}% 1h</span> : null}
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <QuickBuyLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-graduate-buy-${t.tokenMint}`} />
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`cc-graduate-chart-${t.tokenMint}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[10px] ml-1 w-8 text-right" style={{ color: G }}>{t.gradAgeMin}m</span>
                      </div>
                    </div>
                  );
                }
                if (t.kind === "milly_runner") {
                  const GOLD = "#F0C000";
                  const pc = t.priceChange;
                  return (
                    <div
                      key={t.signature}
                      data-testid={`cc-milly-runner-${t.tokenMint}`}
                      className="relative flex items-center gap-3 px-5 py-4 overflow-hidden milly-glow"
                      style={{ borderLeft: `4px solid ${GOLD}`, background: "linear-gradient(90deg, rgba(240,192,0,0.2), rgba(240,192,0,0.03))" }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center border shrink-0 animate-pulse" style={{ borderColor: GOLD, color: GOLD }}>
                        <RocketLaunchIcon size={17} weight="fill" />
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wide font-bold animate-pulse" style={{ background: `${GOLD}33`, border: `1px solid ${GOLD}`, color: GOLD }}>
                            🏃💰 MILLY RUNNER · {t.millyTier}+ SOL
                          </span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                          {typeof t.score === "number" && <span className="text-muted-foreground text-xs">· score {t.score}</span>}
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: GOLD }}>
                          🏃 Running{t.momentumLabel ? ` (${t.momentumLabel.replace(/[^\w\s%×+-]/g, "").trim()})` : ""} · <b>{t.walletLabel || short(t.walletAddress)}</b> loading <b>{t.cumSol} SOL</b>
                          {typeof pc === "number" ? <span className="text-muted-foreground"> · {pc >= 0 ? "+" : ""}{Math.round(pc)}% 24h</span> : null}
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <QuickBuyLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-milly-runner-buy-${t.tokenMint}`} />
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`cc-milly-runner-chart-${t.tokenMint}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[10px] ml-1 w-8 text-right" style={{ color: GOLD }}>now</span>
                      </div>
                    </div>
                  );
                }
                if (t.kind === "cluster") {
                  const CL = "#A78BFA";
                  const names = (t.buyers || []).slice(0, 5).map((b) => ((b.score || 0) >= 60 ? "🔥" : "") + (b.label || short(b.address))).join(", ");
                  return (
                    <div
                      key={t.signature}
                      data-testid={`cc-cluster-${t.tokenMint}`}
                      className="flex items-center gap-3 px-5 py-3.5 hover:bg-white/5 transition-colors"
                      style={{ borderLeft: `3px solid ${CL}`, background: "rgba(167,139,250,0.08)" }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center border shrink-0" style={{ borderColor: CL, color: CL }}>
                        <TargetIcon size={16} weight="fill" />
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wide" style={{ background: `${CL}26`, border: `1px solid ${CL}66`, color: CL }}>
                            🎯 CLUSTER BUY
                          </span>
                          <span className="font-heading font-bold" style={{ color: CL }}>{t.walletCount} wallets</span>
                          <span className="text-muted-foreground text-xs">buying</span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                          {t.proven > 0 && (
                            <span data-testid={`cc-cluster-proven-${t.tokenMint}`} title={`${t.proven} of ${t.walletCount} buyers have a proven ≥60 PnL score`}
                              className="text-[9px] font-mono px-1.5 py-0.5 bg-neon/10 border border-neon/40 text-neon tracking-wide">🔥 {t.proven}/{t.walletCount} proven</span>
                          )}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground truncate">
                          {names}{t.totalSol ? ` · ~${t.totalSol} SOL in` : ""} · last 6h
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <QuickBuyLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-cluster-buy-${t.tokenMint}`} />
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`cc-cluster-chart-${t.tokenMint}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[10px] ml-1 w-8 text-right" style={{ color: CL }}>live</span>
                      </div>
                    </div>
                  );
                }
                if (t.kind === "fastmover") {
                  const up = t.direction === "up";
                  const col = up ? "#00E599" : "#FF3366";
                  return (
                    <div
                      key={t.signature}
                      data-testid={`cc-fastmover-${t.tokenMint}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 transition-colors"
                      style={{ borderLeft: `2px solid ${col}`, background: up ? "rgba(0,229,153,0.06)" : "rgba(255,51,102,0.06)" }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center border shrink-0" style={{ borderColor: col, color: col }}>
                        {up ? <ArrowUpRightIcon size={16} weight="bold" /> : <ArrowDownRightIcon size={16} weight="bold" />}
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wide" style={{ background: `${col}26`, border: `1px solid ${col}66`, color: col }}>
                            {up ? `🚀 ${t.multiplier}× 1h` : `🔻 ${Math.round(t.changeH1)}% 1h`}
                          </span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                          {typeof t.score === "number" && <span className="text-muted-foreground text-xs">· score {t.score}</span>}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {up ? "Pumping fast" : "Dumping fast"}{t.marketCap ? ` · MCap ${fmtVol(t.marketCap)}` : ""} · early gamble
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {up
                          ? <QuickBuyLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-fm-buy-${t.tokenMint}`} />
                          : <QuickSellLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-fm-sell-${t.tokenMint}`} />}
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`cc-fm-chart-${t.tokenMint}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[10px] ml-1 w-8 text-right" style={{ color: col }}>now</span>
                      </div>
                    </div>
                  );
                }
                if (t.kind === "run") {
                  const pc = t.priceChange;
                  return (
                    <div
                      key={t.signature}
                      data-testid={`cc-run-${t.tokenMint}`}
                      className="flex items-center gap-3 px-5 py-3 bg-warn/[0.06] border-l-2 border-warn hover:bg-warn/[0.1] transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center border border-warn/40 text-warn shrink-0">
                        <RocketLaunchIcon size={16} weight="fill" />
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 bg-warn/15 border border-warn/40 text-warn tracking-wide">{t.momentumLabel || "🚀 RUNNING"}</span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-warn transition-colors">{t.tokenSymbol}</Link>
                          {typeof t.score === "number" && <span className="text-muted-foreground text-xs">· score {t.score}</span>}
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          Vol {fmtVol(t.volume24h)}{typeof pc === "number" ? <span style={{ color: pc >= 0 ? "#00E599" : "#FF3366" }}> · {pc >= 0 ? "+" : ""}{Math.round(pc)}% 24h</span> : null}
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <QuickBuyLink coin={{ address: t.tokenMint, url: t.url }} testid={`cc-run-buy-${t.tokenMint}`} />
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-warn transition-colors" data-testid={`cc-run-chart-${t.tokenMint}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[10px] text-warn ml-1 w-8 text-right">now</span>
                      </div>
                    </div>
                  );
                }
                const buy = t.action === "buy";
                const milly = buy && (t.millyTier || 0) >= 5;
                if (milly) {
                  const GOLD = "#F0C000";
                  const stacked = (t.cumSol || 0) > (t.solAmount || 0) + 0.01;
                  return (
                    <div
                      key={`${t.signature}-${t.tokenMint}`}
                      data-testid={`cc-milly-${t.signature}`}
                      className="relative flex items-center gap-3 px-5 py-3.5 overflow-hidden transition-colors milly-glow"
                      style={{ borderLeft: `3px solid ${GOLD}`, background: "linear-gradient(90deg, rgba(240,192,0,0.14), rgba(240,192,0,0.02))" }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center border shrink-0 animate-pulse" style={{ borderColor: GOLD, color: GOLD }}>
                        <CurrencyDollarIcon size={17} weight="bold" />
                      </div>
                      {t.tokenImage ? (
                        <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wide font-bold animate-pulse" style={{ background: `${GOLD}26`, border: `1px solid ${GOLD}99`, color: GOLD }}>
                            💰 MILLY'S CALL · {t.millyTier}+ SOL
                          </span>
                          <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                          <span className="text-muted-foreground text-xs">· {t.walletLabel || short(t.walletAddress)}</span>
                          {provenBadge(t.creator, t.tokenMint)}
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: GOLD }}>
                          {stacked
                            ? <>Stacking → <b>{t.cumSol} SOL</b> in {MILLY_WINDOW_LABEL} (latest ~{t.solAmount})</>
                            : <>Single buy of <b>~{t.solAmount} SOL</b> 🐳</>}
                        </div>
                        <TokenMeta t={t} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <QuickBuyLink coin={{ address: t.tokenMint, url: `https://dexscreener.com/solana/${t.tokenMint}` }} testid={`cc-milly-buy-${t.signature}`} />
                        <Link to={`/coin/${t.tokenMint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`cc-milly-chart-${t.signature}`}>
                          <ChartLineUpIcon size={16} weight="bold" />
                        </Link>
                        <a href={t.url} target="_blank" rel="noreferrer" title="View tx on Solscan" className="p-1.5 text-muted-foreground hover:text-white transition-colors">
                          <ArrowSquareOutIcon size={16} />
                        </a>
                        {trashBtn(t.tokenMint, t.tokenSymbol)}
                        <span className="font-mono text-[11px] ml-1 w-8 text-right" style={{ color: GOLD }}>{timeAgo(t.timestamp)}</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={`${t.signature}-${t.tokenMint}`}
                    data-testid={`cc-event-${t.signature}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 transition-colors"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center border shrink-0 ${buy ? "border-neon/30 text-neon" : "border-danger/30 text-danger"}`}>
                      {buy ? <ArrowDownRightIcon size={16} weight="bold" /> : <ArrowUpRightIcon size={16} weight="bold" />}
                    </div>
                    {t.tokenImage ? (
                      <img src={t.tokenImage} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold" style={{ color: buy ? "#00E599" : "#FF3366" }}>{buy ? "Bought" : "Sold"}</span>
                        <Link to={`/coin/${t.tokenMint}`} className="font-heading font-bold hover:text-neon transition-colors">{t.tokenSymbol}</Link>
                        <span className="text-muted-foreground text-xs">· {t.walletLabel || short(t.walletAddress)}</span>
                        {t.copyWorthy && buy && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 bg-neon/10 border border-neon/30 text-neon tracking-wide">🔥 COPY</span>
                        )}
                        {provenBadge(t.creator, t.tokenMint)}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground">
                        {Number(t.tokenAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })} {t.tokenSymbol} · ~{t.solAmount} SOL{t.source ? ` · ${t.source}` : ""}
                      </div>
                      <TokenMeta t={t} />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {buy
                        ? <QuickBuyLink coin={{ address: t.tokenMint, url: `https://dexscreener.com/solana/${t.tokenMint}` }} testid={`cc-buy-${t.signature}`} />
                        : <QuickSellLink coin={{ address: t.tokenMint, url: `https://dexscreener.com/solana/${t.tokenMint}` }} testid={`cc-sell-${t.signature}`} />}
                      <Link
                        to={`/coin/${t.tokenMint}`}
                        title="Open chart & Degen Score"
                        className="p-1.5 text-muted-foreground hover:text-neon transition-colors"
                        data-testid={`cc-chart-${t.signature}`}
                      >
                        <ChartLineUpIcon size={16} weight="bold" />
                      </Link>
                      <a href={t.url} target="_blank" rel="noreferrer" title="View tx on Solscan" className="p-1.5 text-muted-foreground hover:text-white transition-colors">
                        <ArrowSquareOutIcon size={16} />
                      </a>
                      {trashBtn(t.tokenMint, t.tokenSymbol)}
                      <span className="font-mono text-[11px] text-muted-foreground ml-1 w-8 text-right">{timeAgo(t.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {suppressed.length > 0 && (
            <div data-testid="cc-suppressed" className="border-t border-white/5 px-5 py-2.5 bg-black/20">
              <button
                data-testid="cc-suppressed-toggle"
                onClick={() => setShowHidden((v) => !v)}
                className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground hover:text-danger transition-colors"
              >
                <SkullIcon size={13} weight="fill" className="text-danger" />
                {suppressed.length} community-flagged scam{suppressed.length === 1 ? "" : "s"} auto-hidden
                {showHidden ? <CaretUpIcon size={12} /> : <CaretDownIcon size={12} />}
              </button>
              {showHidden && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {suppressed.map((s) => (
                    <Link key={s.mint} to={`/coin/${s.mint}`} data-testid={`cc-suppressed-${s.mint}`}
                      className="text-[10px] font-mono px-2 py-1 bg-danger/10 border border-danger/30 text-danger hover:bg-danger/20 transition-colors">
                      ☠️ {s.symbol || "token"} · {s.count}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
